/* global setTimeout, clearTimeout, IntersectionObserver */
import L from './leaflet-global.js';
import 'leaflet.markercluster';

(function ($) {
  if (!$('.acf-map').length) {
    return;
  }

  /* The hospital map runs on Leaflet rather than the Google Maps JS API: the
   * library is free and self-hosted, so a page view carries no per-load billing.
   * Tiles still come from somewhere, though — see cmnh_leaflet_tiles in
   * inc/scripts-styles-enqueue.php for pointing production at a paid plan.
   *
   * Marker clustering is what makes 157 hospitals usable on one map. Without it
   * the great majority of pins sit within a pin's width of another at national
   * zoom, which is why per-marker interaction was not viable on a flat image.
   */

  const DEFAULT_TILES = {
    // OpenStreetMap's standard tiles: the only quality basemap that needs no
    // account. CARTO's Positron was the closer match to the grey palette the
    // Google implementation styled by hand, but CARTO now stamps "API KEY
    // REQUIRED" across unkeyed tiles, so it is no longer usable as a default.
    // OSM's tile usage policy discourages heavy traffic, so a busy site should
    // move to a keyed provider through the cmnh_leaflet_tiles filter.
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    subdomains: '',
    maxZoom: 19,
  };

  const MARKER_COLOR = '#FFCC06';
  const US_CENTER = [39.8283, -98.5795];
  const US_ZOOM = 3;
  const SINGLE_MARKER_ZOOM = 12;
  const FIT_PADDING = [32, 32];

  let map = null;
  let clusterGroup = null;
  let markersById = {};
  let updateTimeout = null;
  let startPromise = null;

  function getTileConfig() {
    let cfg = null;

    if (typeof window.cmnLeaflet !== 'undefined' && window.cmnLeaflet) {
      cfg = window.cmnLeaflet.tiles;
    } else if (typeof window.cmnhLeaflet !== 'undefined' && window.cmnhLeaflet) {
      cfg = window.cmnhLeaflet.tiles;
    }

    return cfg && cfg.url ? cfg : DEFAULT_TILES;
  }

  // The teardrop pin carried over from the Google implementation (Font Awesome
  // location-dot). A divIcon keeps it as inline SVG, so it stays sharp at any
  // density and picks up CSS.
  function buildIcon() {
    return L.divIcon({
      className: 'hospital-pin',
      html:
        '<svg viewBox="0 0 384 512" aria-hidden="true" focusable="false">' +
        '<path fill="' +
        MARKER_COLOR +
        '" stroke="' +
        MARKER_COLOR +
        '" stroke-width="12" d="M384 192c0 87.4-117 243-168.3 307.2c-12.3 15.3-35.1 15.3-47.4 0C117 435 0 279.4 0 192C0 86 86 0 192 0S384 86 384 192z"/>' +
        '</svg>',
      iconSize: [24, 32],
      iconAnchor: [12, 32],
      popupAnchor: [0, -30],
    });
  }

  // Reads the hospitals currently rendered in the list, which is the same source
  // the geo search rebuilds. Re-run whenever that list changes.
  function collectMarkerData() {
    const markerDataList = [];
    const locationElements = document.querySelectorAll(
      '.locations-list-items .location'
    );

    for (let i = 0; i < locationElements.length; i++) {
      const el = locationElements[i];

      // Use native style check (faster than jQuery)
      if (el.style.display === 'none') {
        continue;
      }

      const markerEl = el.querySelector('.marker');
      if (!markerEl) {
        continue;
      }

      const lat = parseFloat(markerEl.dataset.lat);
      const lng = parseFloat(markerEl.dataset.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        continue;
      }

      const contentWrap = markerEl.querySelector('.content-wrap');
      const nameEl = markerEl.querySelector('.hospital-name');

      markerDataList.push({
        lat: lat,
        lng: lng,
        businessId: markerEl.dataset.locationId,
        content: contentWrap ? contentWrap.innerHTML : null,
        title: nameEl ? nameEl.textContent.trim() : 'Location',
      });
    }

    return markerDataList;
  }

  function fitToMarkers(markers) {
    if (!map || !markers.length) {
      return;
    }

    if (markers.length === 1) {
      map.setView(markers[0].getLatLng(), SINGLE_MARKER_ZOOM);
      return;
    }

    map.fitBounds(L.featureGroup(markers).getBounds(), {
      padding: FIT_PADDING,
    });
  }

  function updateMarkers() {
    if (!clusterGroup) {
      return;
    }

    clusterGroup.clearLayers();
    markersById = {};

    const data = collectMarkerData();
    const icon = buildIcon();
    const markers = [];

    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const marker = L.marker([item.lat, item.lng], {
        icon: icon,
        title: item.title,
        alt: item.title,
      });

      if (item.content) {
        marker.bindPopup(item.content, { maxWidth: 325 });
      }

      if (item.businessId) {
        markersById[item.businessId] = marker;
      }

      markers.push(marker);
    }

    // addLayers() in one call lets the plugin build the cluster tree once
    // instead of rebalancing on every insert.
    clusterGroup.addLayers(markers);
    fitToMarkers(markers);
  }

  function initMap(mapElement) {
    if (map) {
      updateMarkers();
      return;
    }

    const tiles = getTileConfig();

    map = L.map(mapElement, {
      // Mirrors the old Google options: the page keeps its scroll, and zooming
      // stays a deliberate act rather than something the wheel triggers.
      scrollWheelZoom: false,
      zoomControl: true,
    }).setView(US_CENTER, US_ZOOM);

    L.tileLayer(tiles.url, {
      attribution: tiles.attribution,
      subdomains: tiles.subdomains || 'abc',
      maxZoom: tiles.maxZoom || 19,
    }).addTo(map);

    clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 50,
    });

    map.addLayer(clusterGroup);

    updateMarkers();
  }

  function start(mapElement) {
    if (startPromise) {
      return startPromise;
    }

    startPromise = Promise.resolve().then(function () {
      initMap(mapElement);
    });

    return startPromise;
  }

  function bindLocationClickHandler() {
    $(document).on('click', '.location', function () {
      const businessId = this.dataset.locationId;
      const mapElement = document.querySelector('.acf-map');

      if (!mapElement) {
        return;
      }

      start(mapElement).then(function () {
        const marker = markersById[businessId];

        if (!marker || !clusterGroup) {
          return;
        }

        // The marker may still be folded into a cluster, so expand down to it
        // before opening the popup — this is what keeps clustering compatible
        // with clicking a hospital in the list.
        clusterGroup.zoomToShowLayer(marker, function () {
          marker.openPopup();
        });
      });
    });
  }

  function debouncedUpdateMarkers() {
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }

    updateTimeout = setTimeout(function () {
      const mapElement = document.querySelector('.acf-map');

      if (!mapElement) {
        return;
      }

      start(mapElement).then(function () {
        updateMarkers();
      });
    }, 150);
  }

  window.updateHospitalMapMarkers = debouncedUpdateMarkers;

  $(document).ready(function () {
    const mapElement = document.querySelector('.acf-map');

    if (!mapElement) {
      return;
    }

    bindLocationClickHandler();

    if (typeof IntersectionObserver === 'undefined') {
      start(mapElement);
      return;
    }

    // Hold off until the map nears the viewport: no tiles are requested for a
    // visitor who never scrolls that far.
    const observer = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) {
          return;
        }

        observer.disconnect();
        start(mapElement);
      },
      { rootMargin: '200px' }
    );

    observer.observe(mapElement);
  });
})(jQuery);
